/**
 * Style page — copy helpers and the option sets of its sections
 * (Figma `Style desktop — flat grid`, 194:170754).
 */

import type { HeelHeight } from '@/types/my-style'

/** `Show all 15` appears past this many chips (Brands / Colors sections). */
export const FAIR_PROFILE_V2_SHOW_ALL_LIMIT = 12

/** Figma `Wearing` toggle — Never / Sometimes / Often. */
export const FAIR_PROFILE_V2_HEEL_FREQUENCIES = [
  'never',
  'sometimes',
  'often',
] as const

/** Figma `Preferred height` — up to 4 cm / 5-7 cm / 8+ cm. */
export const FAIR_PROFILE_V2_HEEL_HEIGHTS = ['low', 'mid', 'high'] as const

export type FairProfileV2HeelHeight = (typeof FAIR_PROFILE_V2_HEEL_HEIGHTS)[number]

/** The macket's three heights are the taste survey's own values (BE-139). */
export const FAIR_PROFILE_V2_HEEL_HEIGHT_API: Record<FairProfileV2HeelHeight, HeelHeight> = {
  low: 'up-to-4cm',
  mid: '5-7cm',
  high: '8cm-plus',
}

/** The API's height → the macket's code; an unknown or empty value is no selection. */
export function toFairProfileV2HeelHeight(
  value: string | null | undefined,
): FairProfileV2HeelHeight | null {
  if (!value) return null
  const match = (Object.keys(FAIR_PROFILE_V2_HEEL_HEIGHT_API) as FairProfileV2HeelHeight[]).find(
    (code) => FAIR_PROFILE_V2_HEEL_HEIGHT_API[code] === value || code === value,
  )
  return match ?? null
}

/** One row of the shared add-style / add-colour / add-brand picker. */
export interface FairProfileV2PickerItem {
  id: number
  name: string
  /** Colour swatch, when the catalogue is colours. */
  hex?: string
  /** Logo or style preview, when the catalogue has imagery. */
  image?: string
  /** BE-137 — the style cover's size ladder. */
  imageSrcset?: string
  /** BE-144 — the colour's family code from the API (null: unmapped; absent: older API). */
  family?: string | null
  /** BE-188 — the brand is one of the popular houses. */
  popular?: boolean
}

/** `sizes` of a style cover (BE-137) by where it is shown: the browser picks the 480/960/1792 rung. */
export const FAIR_STYLE_COVER_SIZES = {
  /** Picker tile — 2 columns on a phone, ~160 px on desktop. */
  tile: '(max-width: 600px) 45vw, 160px',
  /** Style card on the hub and My style. */
  card: '(max-width: 600px) 45vw, 220px',
  /** Small row thumb (Rate intro). */
  thumb: '64px',
  /** The DNA drawer's preview. */
  preview: '160px',
} as const

/** Case-insensitive name match; an empty query keeps the whole catalogue. */
export function filterFairProfileV2PickerItems(
  items: FairProfileV2PickerItem[],
  query: string,
): FairProfileV2PickerItem[] {
  const needle = query.trim().toLowerCase()
  if (!needle) {
    return items
  }
  return items.filter((item) => item.name.toLowerCase().includes(needle))
}

/**
 * The taste teaser (Figma `Wardrobe stats teaser`, 1751:72308) sets the server's
 * sentence in two weights: "Mostly Office and Evening" · "with a touch of
 * Sensual". The cut is the first comma; a sentence without one is all headline.
 */
export function splitFairProfileV2TasteLine(summary: string | null | undefined): {
  headline: string
  detail: string
} {
  const text = summary?.trim() ?? ''
  const comma = text.indexOf(',')
  if (comma < 0) return { headline: text, detail: '' }
  return {
    headline: text.slice(0, comma).trim(),
    detail: text.slice(comma + 1).trim(),
  }
}
